import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './axiosBaseQuery';
import { apiURL } from '../config/default';

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: axiosBaseQuery({ baseUrl: apiURL }),
  // Global cache duration: 2 minutes (optimized for admin dashboards)
  keepUnusedDataFor: 120, 
  tagTypes: ['Settings', 'PublicSettings'],
  endpoints: (builder) => ({
    
    // --- PUBLIC ENDPOINTS (No Auth Required) ---
    
    getPublicSettings: builder.query({
      // Handles both "Get All Public" and "Get Public by Keys" dynamically
      query: (keys) => ({
        url: 'admin/global-settings/public',
        method: 'GET',
        params: keys ? { keys: Array.isArray(keys) ? keys.join(',') : keys } : {},
      }),
      providesTags: ['PublicSettings'],
    }),

    // --- ADMIN READ OPERATIONS ---

    getAllSettings: builder.query({
      query: ({ page = 1, limit = 50, category, isPublic, search } = {}) => ({
        url: 'admin/global-settings',
        method: 'GET',
        params: { page, limit, category, isPublic, search },
      }),
      // Advanced Tagging: Allows granular cache invalidation
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ key }) => ({ type: 'Settings', id: key })),
              { type: 'Settings', id: 'LIST' },
            ]
          : [{ type: 'Settings', id: 'LIST' }],
    }),

    getSettingByKey: builder.query({
      query: (key) => ({
        url: `admin/global-settings/${key}`,
        method: 'GET',
      }),
      providesTags: (result, error, key) => [{ type: 'Settings', id: key }],
    }),

    // NEW: Added from Collection - Get Multiple Settings by Keys
    getMultipleSettingsByKeys: builder.query({
      query: (keys) => ({
        url: 'admin/global-settings/keys',
        method: 'POST',
        data: { keys: Array.isArray(keys) ? keys : [keys] },
      }),
      providesTags: (result) => 
        result?.data 
          ? Object.keys(result.data).map(key => ({ type: 'Settings', id: key }))
          : [{ type: 'Settings', id: 'LIST' }],
    }),

    getSettingsByCategory: builder.query({
      query: (category) => ({
        url: `admin/global-settings/category/${category}`,
        method: 'GET',
      }),
      providesTags: (result, error, category) => [{ type: 'Settings', id: `CAT-${category}` }],
    }),

    // --- ADMIN WRITE OPERATIONS (Fully Optimized Cache Invalidation) ---

    upsertSetting: builder.mutation({
      query: (settingData) => ({
        url: 'admin/global-settings',
        method: 'POST',
        data: settingData,
      }),
      // Re-fetches only what changed + the public view
      invalidatesTags: (result, error, { key }) => [
        { type: 'Settings', id: key },
        { type: 'Settings', id: 'LIST' },
        'PublicSettings'
      ],
    }),

    bulkUpsertSettings: builder.mutation({
      query: (settings) => ({
        url: 'admin/global-settings/bulk',
        method: 'POST',
        data: { settings },
      }),
      invalidatesTags: ['Settings', 'PublicSettings'], // Global refresh for bulk
    }),

    patchSettingValue: builder.mutation({
      query: ({ key, value }) => ({
        url: `admin/global-settings/${key}`,
        method: 'PATCH',
        data: { value },
      }),
      invalidatesTags: (result, error, { key }) => [
        { type: 'Settings', id: key },
        { type: 'Settings', id: 'LIST' },
        'PublicSettings'
      ],
    }),

    deleteSetting: builder.mutation({
      query: (key) => ({
        url: `admin/global-settings/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, key) => [
        { type: 'Settings', id: key },
        { type: 'Settings', id: 'LIST' },
        'PublicSettings'
      ],
    }),

    bulkDeleteSettings: builder.mutation({
      query: (keys) => ({
        url: 'admin/global-settings/bulk/delete',
        method: 'DELETE',
        data: { keys },
      }),
      invalidatesTags: ['Settings', 'PublicSettings'],
    }),
  }),
});

export const {
  useGetPublicSettingsQuery,
  useGetAllSettingsQuery,
  useGetSettingByKeyQuery,
  useGetMultipleSettingsByKeysQuery, // New Hook
  useGetSettingsByCategoryQuery,
  useUpsertSettingMutation,
  useBulkUpsertSettingsMutation,
  usePatchSettingValueMutation,
  useDeleteSettingMutation,
  useBulkDeleteSettingsMutation,
} = settingsApi;